<script lang="ts">
    import { onMount } from 'svelte/internal'
    import Fa from 'svelte-fa'
    import { faPlusCircle, faUser, faEdit, faFile, faPrint, faLink, faTrash, faSignature } from '@fortawesome/free-solid-svg-icons'
    import { FormDatasRepository } from '../../modules/toolsApi/forms/form-datas/form-datas.repository'
    import { FormDatasService } from '../../modules/toolsApi/forms/form-datas/form-datas.service'
    import type { FormData } from '../../modules/toolsApi/forms/form-datas/form-data'
    import type { FormV2Form } from '../../modules/toolsApi/forms-v2/form-v2-forms/form-v2-form'
    import { printFormV2 } from '../customer-service-v2/forms/form-v2-print.helper'
    import FormV2FormLinkPopover from '../customer-service-v2/forms/form-v2-form-link-popover.svelte'
    import FormV2SignPopover from '../customer-service-v2/forms/form-v2-sign-popover.svelte'
    import Popover from '../_common/popover.svelte'
    import { GridBadge, GridButton, GridClass, GridCol, GridColTypeEnum, GridLine } from '../_common/z/zgrid'
    import Zgrid from '../_common/z/zgrid.svelte'
    // ##############################################################################################################

    export let personId: string

    const rep = new FormDatasRepository()
    const service = new FormDatasService(rep)

    const showDocumentVersionLatest = window.configs?.documentVersionLatest

    const FORM_V2_DRAFT = 60
    const FORM_V2_COMPLETED = 100
    const FORM_V2_SIGNING = 105
    const FORM_V2_SIGNED = 110

    let show = false
    let entities: FormData[] = []
    let latestEntities: Pick<FormV2Form, 'formV2FormId' | 'title' | 'status' | 'signatureRequested' | 'createdAt'>[] = []
    let latestLinkFormId: string | null = null
    let latestLinkFormCompleted = false
    let latestSignFormId: string | null = null

    async function loadEntities() {
        try {
            show = true
            entities = await service.getAll({ personId })
            loadGrid()
            console.log('entities', entities)
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    function getStatusLabel(status: number) {
        if (status === 60) return 'Rascunho'
        if (status === 110) return 'Assinado'
        return 'Completo'
    }

    async function loadLatestEntities() {
        try {
            latestEntities = await window.api.tools.formsV2.forms.getAll({ personId })
            loadLatestGrid()
        } catch (error) {
            window.error(error)
        }
    }

    function getLatestStatusLabel(status: number) {
        if (status === FORM_V2_DRAFT) return 'Preenchendo'
        if (status === FORM_V2_COMPLETED) return 'Concluído'
        if (status === FORM_V2_SIGNING) return 'Assinando'
        if (status === FORM_V2_SIGNED) return 'Assinado'
        return 'Desconhecido'
    }

    let latestGrid: GridClass = new GridClass()
    function loadLatestGrid() {
        latestGrid = new GridClass()
        latestGrid.title = 'Atendimentos Realizados'
        latestGrid.subtitle = ''
        latestGrid.fontSize = '15px'
        if (window.store.token.permissions.customerService.entries.write) {
            latestGrid.acts = [
                new GridButton({
                    label: 'Novo atendimento',
                    icon: faPlusCircle,
                    mobileShowLabel: false,
                    class: 'btn btn-primary',
                    callback: () => {
                        newLatestForm()
                    },
                }),
            ]
        }
        latestGrid.cols.push(new GridCol({ content: 'Status', type: GridColTypeEnum.badge }))
        latestGrid.cols.push(new GridCol({ content: 'Data', type: GridColTypeEnum.datetime }))
        latestGrid.cols.push(new GridCol({ content: 'Modelo', type: GridColTypeEnum.string, maxLength: 45 }))
        latestGrid.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button }))

        // Mesmos botões/regras de cs-v2-forms.svelte (a listagem "completa" de Forms V2) — só que aqui
        // imprimir/link/excluir ficam só com ícone, sem espaço pro rótulo.
        for (const item of latestEntities) {
            let line: GridLine = new GridLine()
            line.slots.push({
                content: new GridBadge({
                    label: getLatestStatusLabel(item.status),
                    class: 'status_' + item.status,
                }),
            })
            line.slots.push({ content: item.createdAt })
            line.slots.push({ content: item.title })

            const locked = item.status >= FORM_V2_SIGNING
            const buttons: GridButton[] = []
            if (!locked && window.store.token.permissions.customerService.entries.write) {
                buttons.push(new GridButton({ icon: faEdit, label: 'Editar', callback: () => editLatestForm(item.formV2FormId) }))
            }
            if (window.store.token.permissions.customerService.entries.read) {
                buttons.push(new GridButton({ icon: faEdit, label: 'Ver', callback: () => showLatestForm(item.formV2FormId) }))
            }
            // Iniciar assinatura exige modelo com "Solicitar assinatura ao preencher"; documentos já em
            // assinatura mantêm o botão para acessar PDF assinado, relatório e histórico (mesma regra
            // do grid de atendimentos).
            const canSign = item.status >= FORM_V2_SIGNING || (item.status === FORM_V2_COMPLETED && item.signatureRequested)
            if (canSign && window.store.token.permissions.customerService.entries.write) {
                buttons.push(new GridButton({ icon: faSignature, label: 'Assinatura', callback: () => openLatestSign(item.formV2FormId) }))
            }
            if (window.store.token.permissions.customerService.entries.read) {
                buttons.push(new GridButton({ icon: faLink, callback: () => openLatestShare(item.formV2FormId, item.status) }))
                buttons.push(new GridButton({ icon: faPrint, callback: () => printLatestForm(item.formV2FormId) }))
            }
            if (!locked && window.store.token.permissions.customerService.entries.write) {
                buttons.push(new GridButton({ icon: faTrash, callback: () => removeLatestForm(item.formV2FormId) }))
            }
            line.slots.push({ content: buttons })
            latestGrid.lines.push(line)
        }
    }

    function newLatestForm() {
        window.popover({
            act: 'customer-service-v2',
            data: { personId, view: false },
            callback: () => latestFormChanged(),
        })
    }

    function editLatestForm(id: string) {
        window.popover({
            act: 'customer-service-v2',
            data: { personId, formV2FormId: id, view: false },
            callback: () => latestFormChanged(),
        })
    }

    function showLatestForm(id: string) {
        window.popover({
            act: 'customer-service-v2',
            data: { personId, formV2FormId: id, view: true },
            callback: () => latestFormChanged(),
        })
    }

    function openLatestShare(id: string, status: number) {
        latestLinkFormId = id
        latestLinkFormCompleted = status === FORM_V2_COMPLETED
    }

    function openLatestSign(id: string) {
        latestSignFormId = id
    }

    async function closeLatestSign() {
        latestSignFormId = null
        await loadLatestEntities()
    }

    async function removeLatestForm(id: string) {
        if (!confirm('Deseja mesmo excluir este preenchimento?')) return
        try {
            show = true
            await window.api.tools.formsV2.forms.remove(id)
            await loadLatestEntities()
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    async function printLatestForm(id: string) {
        try {
            show = true
            await printFormV2(id)
        } finally {
            show = false
        }
    }

    async function latestFormChanged() {
        await loadLatestEntities()
    }

    let grid: GridClass = new GridClass()
    function loadGrid() {
        // grid
        grid = new GridClass()
        // grid head
        grid.title = 'Atendimentos (versão legada)'
        grid.subtitle = ''
        grid.fontSize = '15px'
        if (window.store.token.permissions.customerService.entries.write) {
            grid.acts = [
                new GridButton({
                    label: 'Novo atendimento',
                    icon: faPlusCircle,
                    mobileShowLabel: false,
                    class: 'btn btn-primary',
                    callback: () => {
                        newForm()
                    },
                }),
            ]
        }
        grid.cols.push(new GridCol({ content: 'Status', type: GridColTypeEnum.badge }))
        grid.cols.push(new GridCol({ content: 'Data', type: GridColTypeEnum.datetime }))
        grid.cols.push(new GridCol({ content: 'Modelo', type: GridColTypeEnum.string, maxLength: 45 }))
        const showScore = entities.some((el) => el.scoreMax > 0)
        if (showScore) {
            grid.cols.push(new GridCol({ content: 'Pontuação', type: GridColTypeEnum.string }))
        }
        grid.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button }))
        // grid lines
        for (const item of entities) {
            const btnShow = new GridButton({
                callback: () => showCustomerService(item.formDataId),
                icon: faEdit,
                label: 'Ver',
            })
            const btnEdit = new GridButton({
                callback: () => editCustomerService(item.formDataId),
                icon: faEdit,
                label: 'Editar',
            })
            let line: GridLine = new GridLine()
            line.slots.push({
                content: new GridBadge({
                    label: getStatusLabel(item.status),
                    class: 'status_' + item.status,
                }),
            })
            line.slots.push({ content: item.createdAt })
            line.slots.push({ content: item.template.title })
            if (showScore) {
                line.slots.push({ content: item.scoreMax ? item.score.toString() + '/' + item.scoreMax.toString() : '' })
            }

            let buttons = []
            if (window.store.token.permissions.customerService.entries.read) {
                buttons.push(btnShow)
            }
            if (window.store.token.permissions.customerService.entries.write) {
                buttons.push(btnEdit)
            }
            line.slots.push({ content: buttons })
            grid.lines.push(line)
        }
    }

    function newForm() {
        window.popover({
            act: 'customer-service',
            data: { personId, view: false },
            callback: () => formChanged(),
        })
    }

    function editCustomerService(id: string) {
        window.popover({
            act: 'customer-service',
            data: { personId, formDataId: id, view: false },
            callback: () => formChanged(),
        })
    }

    function showCustomerService(id: string) {
        window.popover({
            act: 'customer-service',
            data: { personId, formDataId: id, view: true },
            callback: () => formChanged(),
        })
    }

    async function formChanged() {
        await loadEntities()
    }

    onMount(async () => {
        const tasks = [loadEntities()]
        if (showDocumentVersionLatest) tasks.push(loadLatestEntities())
        await Promise.all(tasks)
    })
</script>

{#if showDocumentVersionLatest}
    <Zgrid grid={latestGrid} class="mb-3"></Zgrid>
{/if}
<Zgrid {grid}></Zgrid>

{#if latestLinkFormId}
    <Popover title="Gerar link público" width="580px" on:close={() => (latestLinkFormId = null)}>
        <FormV2FormLinkPopover formV2FormId={latestLinkFormId} completed={latestLinkFormCompleted} />
    </Popover>
{/if}

{#if latestSignFormId}
    <Popover title="Assinatura do documento" width="740px" on:close={closeLatestSign}>
        <FormV2SignPopover formV2FormId={latestSignFormId} on:refresh={loadLatestEntities} />
    </Popover>
{/if}
